import { useEffect, useState } from 'react';

import { useRouter } from 'next/router';

import {
  createUrlWithSourceQueryParams,
  trackUserActivity,
  UserActivityEvent,
} from '@crehana/analytics';
import { unescapeChars } from '@crehana/ui/dist/StringParser';
import { customLocalStorage, customSessionStorage, date } from '@crehana/utils';
import { useMe } from '@crehana/web';

import { baseRedirectUrlToDownloadableForm } from '@/views/B2BLanding/constants';
import { buildStaticResourceUtms } from '@/views/B2BLanding/utils/buildStaticResourceUtms';
import { joinQueryParams } from '@/views/B2BLanding/utils/joinQueryParams';

import {
  B2B_RESOURCES_DOWNLOADABLE_DETAIL,
  itemTypes,
} from '../../../constants';
import { GetDownloadableResourceDetail_resource as ResourceType } from '../../../graphql/types/GetDownloadableResourceDetail';
import useDownloadResource from '../../../hooks/useDownloadResource';
import useGetResource from '../../../hooks/useGetResource';
import Buttons from './Buttons';
import ImageResource from './ImageResource';
import { Article, ContentWrapper } from './Resource.styles';
import Tags from './Tags';

export const getResourceItemType = (category?: string) =>
  itemTypes[category || 'herramientas'];

const Resource = () => {
  const { loading: loadingMe } = useMe();
  const router = useRouter();
  const { slug } = router.query as { slug: string };
  const resourceData = useGetResource(slug);
  const { onDownload } = useDownloadResource(
    resourceData?.downloadUrlB2B!,
    resourceData?.downloadUrl,
  );
  const [loadingRedirect, setLoadingRedirect] = useState<boolean>(false);
  const { days } = date.differenceBetweenDates(
    resourceData?.publicationDate,
    new Date(),
  );

  const isNew = days < 7;
  const resourceCategory = resourceData?.resourceCategory?.name;
  const category = resourceData?.category;
  const publicationDate = resourceData?.publicationDate;
  const richContent = resourceData?.richContent;

  const goFreeTrial = (resource: ResourceType) => {
    setLoadingRedirect(true);

    if (router.query?.hasCompletedForm === 'true') {
      onDownload();
      return;
    }

    const itemType = getResourceItemType(resource.category as string);
    customSessionStorage.setItem('resourceType', itemType);
    customSessionStorage.setItem('downloadUrlB2B', resource.downloadUrlB2B!);
    customSessionStorage.setItem('detailResourceUrl', slug);
    customLocalStorage.setItem('detailResourceUrl', slug);
    customLocalStorage.setItem('downloadUrlB2B', resource.downloadUrlB2B!);
    trackUserActivity({
      event: UserActivityEvent.DOWNLOAD_RESOURCE,
      data: {
        product_id: resource.originalId as number,
        product_name: resource.title,
        item_type: itemType as 'tool' | 'ebook',
        origin_path: window.location.href,
      },
    });

    const url = createUrlWithSourceQueryParams({
      url: joinQueryParams(
        `${baseRedirectUrlToDownloadableForm}?slug=${slug}&resource_type=${itemType}`,
        router.asPath,
        buildStaticResourceUtms({
          urlResource: slug,
          resourceName: resource.title,
          term: 'descargar',
        }),
      ),
      source: B2B_RESOURCES_DOWNLOADABLE_DETAIL,
      sourceDetail: resourceCategory,
    });
    router.push(url);
  };

  const handleGoToResource = () => {
    if (resourceData) {
      goFreeTrial(resourceData);
    }
  };

  useEffect(() => {
    const element = document.getElementById('download-button');

    if (element) {
      element.addEventListener('click', handleGoToResource);
    }

    return () => {
      if (element) {
        element.removeEventListener('click', handleGoToResource);
      }
    };
  }, [richContent]);

  return (
    <>
      <section className="flex flex-col md:flex-row mx-auto">
        {resourceData?.coverUrl && (
          <ImageResource coverUrl={resourceData.coverUrl} />
        )}
        <Article className="mt-24 px-24 sm:px-0 md:mt-0">
          <h1 className="mb-24">
            <span className="text-24 leading-28 tracking-1 tw-font-pop tw-font-semibold md:hidden">
              {resourceData?.title}
            </span>
            <span className="font-h3 hidden md:block">
              {resourceData?.title}
            </span>
          </h1>

          <div className="flex items-center flex-wrap justify-between md:flex-col md:items-start">
            {resourceCategory && category ? (
              <Tags
                isNew={isNew}
                resourceCategory={resourceCategory}
                category={category}
              />
            ) : null}
            {publicationDate ? (
              <p className="font-body4 text-gray-dark mb-24">
                {date.format(publicationDate, 'DD MMMM YYYY')}
              </p>
            ) : null}
          </div>
          {resourceData?.description ? (
            <p
              className="font-nun text-18 leading-24 text-base-main"
              dangerouslySetInnerHTML={{
                __html: unescapeChars(resourceData.description),
              }}
            />
          ) : null}

          {resourceData && !loadingMe ? (
            <Buttons
              onClick={() => goFreeTrial(resourceData!)}
              downloadNumber={resourceData.downloads}
              loading={loadingRedirect}
            />
          ) : null}
        </Article>
      </section>
      {richContent ? (
        <ContentWrapper
          className="p-20 pt-24 md:pt-32"
          dangerouslySetInnerHTML={{ __html: unescapeChars(richContent) }}
        />
      ) : null}
    </>
  );
};

export default Resource;
